import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { collection, onSnapshot, doc, setDoc, deleteDoc, query, orderBy, getDocs } from 'firebase/firestore';
import { StoreItem, SaleHistory, StoreItemType } from '../../types';
import { ShoppingBag, DollarSign, Package, Tag, Search, Plus, Trash2, Edit2, TrendingUp, Users } from 'lucide-react';

const StoreManager: React.FC = () => {
    const [items, setItems] = useState<StoreItem[]>([]);
    const [sales, setSales] = useState<SaleHistory[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'products' | 'sales'>('products');

    // Mocks / Fetch for Items
    useEffect(() => {
        if (!db) return;
        const unsubscribe = onSnapshot(collection(db, 'store_items'), (snapshot) => {
            const itemsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as StoreItem));
            setItems(itemsList);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Form State
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: 'lupa' as StoreItemType,
        price: '',
        discount: '',
        rewardValue: '',
        isActive: true,
        highlight: '',
        iconUrl: ''
    });

    const resetForm = () => {
        setIsEditing(false);
        setCurrentId('');
        setFormData({
            title: '', description: '', type: 'keys', price: '', discount: '', rewardValue: '', isActive: true, highlight: '', iconUrl: ''
        });
    };

    const handleEdit = (item: StoreItem) => {
        setIsEditing(true);
        setCurrentId(item.id);
        setFormData({
            title: item.title,
            description: item.description,
            type: item.type,
            price: item.price.toString(),
            discount: item.discount?.toString() || '',
            rewardValue: item.rewardValue.toString(),
            isActive: item.isActive,
            highlight: item.highlight || '',
            iconUrl: item.iconUrl || ''
        });
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Tem certeza que deseja apagar este pacote da loja?")) return;
        try {
            await deleteDoc(doc(db, 'store_items', id));
        } catch (error) {
            console.error(error);
            alert("Erro ao excluir.");
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const idToSave = isEditing ? currentId : `item_${Date.now()}`;
            const parsedPrice = parseFloat(formData.price.replace(',', '.')) || 0;
            const payload: any = {
                id: idToSave,
                title: formData.title,
                description: formData.description,
                type: formData.type,
                price: parsedPrice,
                discount: formData.discount ? parseInt(formData.discount) : null,
                rewardValue: formData.type === 'case' ? formData.rewardValue : parseInt(formData.rewardValue.toString() || '0'),
                isActive: formData.isActive,
                highlight: formData.highlight || null,
                iconUrl: formData.iconUrl || null
            };
            if (!isEditing) {
                payload.createdAt = new Date();
            }

            await setDoc(doc(db, 'store_items', idToSave), payload, { merge: true });
            resetForm();
            alert("Produto salvo com sucesso!");
        } catch (error) {
            console.error(error);
            alert("Erro ao salvar produto.");
        }
    };

    return (
        <div className="animate-in fade-in duration-500 flex flex-col h-full max-h-full">
            <div className="flex justify-between items-center mb-6 shrink-0">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <ShoppingBag className="text-amber-600" /> Loja & Financeiro
                    </h1>
                    <p className="text-gray-500 mt-1">Gerencie os pacotes da lojinha ACME e acompanhe suas vendas</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-6 shrink-0">
                <button
                    onClick={() => setActiveTab('products')}
                    className={`px-6 py-3 font-bold border-b-2 transition-colors ${activeTab === 'products' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    <div className="flex items-center gap-2"><Package size={18} /> Cadastrar Produtos (Estoque)</div>
                </button>
                <button
                    onClick={() => setActiveTab('sales')}
                    className={`px-6 py-3 font-bold border-b-2 transition-colors ${activeTab === 'sales' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    <div className="flex items-center gap-2"><DollarSign size={18} /> Histórico de Vendas</div>
                </button>
            </div>

            {activeTab === 'products' ? (
                <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
                    {/* Lista de Produtos */}
                    <div className="lg:w-1/2 flex flex-col min-h-0 bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                            <h2 className="font-bold text-gray-700">Produtos Cadastrados ({items.length})</h2>
                            {isEditing && (
                                <button onClick={resetForm} className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded font-bold">
                                    + NOVO PRODUTO
                                </button>
                            )}
                        </div>
                        <div className="overflow-auto flex-1 p-4 space-y-3">
                            {items.length === 0 ? (
                                <div className="text-center py-8 text-gray-400">Nenhum produto cadastrado na vitrine.</div>
                            ) : (
                                items.map(item => (
                                    <div key={item.id} className={`p-4 border rounded relative flex justify-between items-start transition-colors ${!item.isActive ? 'bg-gray-50 border-gray-200 opacity-60' : currentId === item.id ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-amber-200'}`}>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase text-white ${item.type === 'lupa' ? 'bg-green-500' : item.type === 'vip' ? 'bg-amber-500' : item.type === 'keys' ? 'bg-yellow-600' : 'bg-purple-500'}`}>
                                                    {item.type}
                                                </span>
                                                <h3 className="font-bold text-gray-800">{item.title}</h3>
                                                {item.highlight && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded border border-red-200">{item.highlight}</span>}
                                            </div>
                                            <p className="text-xs text-gray-500 mb-2">{item.description}</p>
                                            <div className="flex items-center gap-3 text-sm font-bold mt-2">
                                                <span className="text-gray-900">R$ {item.price.toFixed(2)}</span>
                                                {item.discount && <span className="text-green-600 text-xs">(-{item.discount}%)</span>}
                                                <span className="text-gray-400 font-normal text-xs ml-2">Entrega: {item.rewardValue}</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleEdit(item)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit2 size={16} /></button>
                                            <button onClick={() => handleDelete(item.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Editor de Produto */}
                    <div className="lg:w-1/2 overflow-auto bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                            {isEditing ? <Edit2 className="text-blue-500" /> : <Plus className="text-green-500" />}
                            {isEditing ? 'Editar Produto' : 'Criar Novo Produto'}
                        </h2>

                        <form onSubmit={handleSave} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Título do Produto *</label>
                                    <input required type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:border-amber-500 outline-none" placeholder="Ex: Pacote de Investigador" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tipo *</label>
                                    <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value as StoreItemType })} className="w-full p-2 border border-gray-300 rounded focus:border-amber-500 outline-none">
                                        <option value="lupa">Lupas Extras (Energia)</option>
                                        <option value="vip">Assinatura VIP (Dias)</option>
                                        <option value="keys">Chaves de Arquivo (Acesso Adicional)</option>
                                        <option value="case">Caso Específico (Passaporte)</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descrição Comercial</label>
                                <textarea required value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:border-amber-500 outline-none h-20 resize-none" placeholder="Benefícios que o jogador vai receber..." />
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Preço (R$) *</label>
                                    <input required type="number" step="0.01" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:border-amber-500 outline-none" placeholder="4.90" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Desconto Tag (%)</label>
                                    <input type="number" value={formData.discount} onChange={e => setFormData({ ...formData, discount: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:border-amber-500 outline-none" placeholder="Ex: 20" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-amber-600 uppercase mb-1">Qtd Entregue *</label>
                                    <input required type={formData.type === 'case' ? 'text' : 'number'} value={formData.rewardValue} onChange={e => setFormData({ ...formData, rewardValue: e.target.value })} className="w-full p-2 border border-amber-300 bg-amber-50 rounded focus:border-amber-500 outline-none font-bold text-amber-900" placeholder={formData.type === 'lupa' ? "Qtd de Lupas" : formData.type === 'vip' ? "Dias de VIP" : formData.type === 'keys' ? "Qtd de Chaves" : "ID do Caso"} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-2">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tag Destaque (Opcional)</label>
                                    <input type="text" value={formData.highlight} onChange={e => setFormData({ ...formData, highlight: e.target.value })} className="w-full p-2 text-sm border border-gray-300 rounded focus:border-amber-500 outline-none" placeholder="Ex: MAIS POPULAR" />
                                </div>
                                <div className="flex items-end pb-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} className="w-5 h-5 accent-amber-600" />
                                        <span className="font-bold text-sm text-gray-700">Produto Ativo (Visível)</span>
                                    </label>
                                </div>
                            </div>

                            <button type="submit" className="w-full mt-4 bg-amber-600 hover:bg-amber-500 text-white font-bold py-3 rounded transition-colors shadow-md">
                                {isEditing ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR PRODUTO NA LOJA'}
                            </button>
                        </form>
                    </div>
                </div>
            ) : (
                /* Histórico de Vendas (Simulação Inicial) */
                <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center flex-1 flex flex-col items-center justify-center min-h-[400px]">
                    <TrendingUp className="w-16 h-16 text-gray-200 mb-4" />
                    <h2 className="text-xl font-bold text-gray-700 mb-2">Histórico de Transações</h2>
                    <p className="text-gray-500 max-w-md">O livro caixa capturará automaticamente e validará transações reais quando os Webhooks do Asaas Pix forem implementados e vinculados.</p>
                </div>
            )}
        </div>
    );
};

export default StoreManager;
